import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import PageModal from '../../common/PageModal'
import SimpleButton from '../../common/SimpleButton'
import ToggleSwitch from '../../common/ToggleSwitch'
import ExpandableSection from '../../common/ExpandableSection'
import TooltipedIcon from '../../common/TooltipedIcon'
import { videosUploadWritePermission } from '../../../utils/permissionValidation'
import { capitalizeFirstLetter, capitalizeWords } from '../../../utils/functions'
import countries from '../../../utils/countries.json'
import { languagesShort } from '../../../utils/languages'
import { InformationIcon, UrlIcon, VideoPlayIcon } from '../../../icons'
import { formatDateMonthDayYear } from '../../../utils/filters/date'
import '../uploads.scss'

const ViewVideoModal = (props) => {
  const { t } = useTranslation()
  const { selectedVideo, handleEditVideo, closeModal } = props
  const [expandedItem, setExpandedItem] = useState(null)
  const videoStatuses = ['inactive', 'active']

  const videoHeaderContent = () => {
    return (
      <div className="status-wrapper">
        <TooltipedIcon icon={<InformationIcon />} tooltipText={`${t('video_context.status_toggle_text')}`} orientation="bottom" />
        <div className="label">{t('status')}</div>
        <ToggleSwitch checked={selectedVideo.isActive} isDisabled={true} />
        <div className="status">{t(videoStatuses[Number(selectedVideo.isActive)])}</div>
      </div>
    )
  }

  const videoFooterContent = () => {
    return (
      <>
        <SimpleButton className="cancel-button" onClick={closeModal}>
          {t('close')}
        </SimpleButton>
        <SimpleButton className="action-button" onClick={handleEditVideo} requiredPermission={videosUploadWritePermission}>
          {t('edit')}
        </SimpleButton>
      </>
    )
  }

  const toggleExpand = (id, index) => {
    setExpandedItem(expandedItem === `${id}-${index}` ? null : `${id}-${index}`)
  }

  return (
    <>
      <PageModal
        toggle
        onToggle={closeModal}
        className="uploads-modal"
        title={t('details')}
        headerContent={videoHeaderContent()}
        footerContent={videoFooterContent()}
      >
        <div className="uploads-modal-content">
          <div className="section-title">{t('general_information').toUpperCase()}</div>
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{t('title')}</p>
              <div className="value">{selectedVideo.title}</div>
            </div>
          </div>
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{t('description')}</p>
              <div className="value">{selectedVideo.description}</div>
            </div>
          </div>
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{t('url')}</p>
              <div className="value">
                <UrlIcon />
                {selectedVideo.url}
              </div>
            </div>
          </div>
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{t('video_id')}</p>
              <div className="value">{selectedVideo.videoId}</div>
            </div>
          </div>
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{t('video_context.thumbnail_url')}</p>
              <div className="video-url-wrapper">
                <div className="thumbnail-mask">
                  <div className="thumbnail-icon">
                    <VideoPlayIcon />
                  </div>
                  <img className="thumbnail" src={selectedVideo.thumbnailUrl} alt={selectedVideo.title} />
                </div>
                <div className="value">{selectedVideo.thumbnailUrl}</div>
              </div>
            </div>
          </div>

          <div className="separator" />

          <div className="section-title">{t('settings').toUpperCase()}</div>
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{t('access_level')}</p>
              <div className="value">{capitalizeFirstLetter(selectedVideo.accessLevel)}</div>
            </div>
            <div className="input-wrapper">
              <p className="input-option">{t('video_context.video_type')}</p>
              <div className="value">{capitalizeFirstLetter(selectedVideo.videoType)}</div>
            </div>
          </div>

          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{t('categories')}</p>
              <div className="ordered-list-wrapper">
                <div className="columns-wrapper">
                  {selectedVideo.categories?.map((category, index) => (
                    <div className="value" key={index}>
                      {category}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{t('regions')}</p>
              <div className="ordered-list-wrapper">
                <div className="columns-wrapper">
                  {selectedVideo.regions?.map((region, index) => {
                    return (
                      <div className="value" key={index}>
                        {region.name}
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          </div>
          {false &&
            selectedVideo.regions?.map((region, index) => {
              return (
                <div className="row-wrapper" key={`countries-${index}`}>
                  <ExpandableSection
                    title={`${region?.name} ${t('countries')}`}
                    isExpanded={expandedItem === `${selectedVideo.id}-${index}`}
                    onToggle={() => toggleExpand(selectedVideo.id, index)}
                    className="internal-expandable"
                  >
                    <div className="ordered-list-wrapper">
                      <div className="columns-wrapper">
                        {region.countries?.includes('ALL') ? (
                          <div className="value">{t('all')}</div>
                        ) : (
                          region.countries?.map((country, index) => (
                            <div className="value is-read-only" key={index}>
                              {countries.find((c) => c.code === country)?.name}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </ExpandableSection>
                </div>
              )
            })}
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{t('language')}</p>
              <div className="value">{`${t(languagesShort[selectedVideo.languageCode.toLowerCase()] || selectedVideo.languageCode)} (${selectedVideo.languageCode})`}</div>
            </div>
            <div className="input-wrapper">
              <p className="input-option">{capitalizeWords(t('last_updated'))}</p>
              <div className="value">{selectedVideo.videoLastUpdateDate ? formatDateMonthDayYear(selectedVideo.videoLastUpdateDate) : ''}</div>
            </div>
          </div>

          <div className="separator" />

          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{t('monitoring.models')}</p>
              <div className="ordered-list-wrapper">
                <div className="columns-wrapper">
                  {selectedVideo.models?.map((model, index) => (
                    <div className="value" key={index}>
                      {model}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </PageModal>
    </>
  )
}

export default ViewVideoModal

